import { useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Slide, Lightbox } from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen';
import { Helmet } from 'react-helmet-async';

import styles from './Favorite.module.css';

import { FavoriteArticleContext } from './Favorite.context';
import {
	useArticle,
	useValidateFavorite,
	useFavorites,
} from './Favorite.hooks';

import { useFont } from '../issue/Issue.hooks';
import { Menu } from '../../components/menu/Menu';
import { Button } from '../issue/menu-button/MenuButton';

import { NAVBAR_ICON_URL } from '../../constants';
import { AppContext } from '../../App.context';

import { useTrackingPageView } from '../../tracking/Tracking.hooks';

import { Icon } from '../../components/icon/Icon';
import { Loading } from '../../components/loading/Loading';
import { useStyle } from '../../components/article/virtual/VirtualArticles';
import { VirtualArticle } from '../../components/article/virtual/VirtualArticle';
import { LightboxArgs } from '../../components/article/virtual/VirtualArticleContext';
import { AssetsContext } from '../../assets/Assets.context';

const Favorite = () => {
	const { id } = useParams();
	const { state } = useLocation();
	const navigate = useNavigate();
	const { fontSize, setFontSize } = useFont();
	const { favorites, setFavorites } = useFavorites();
	const { article, isLoading, error } = useArticle({ id });
	const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);

	const onBack = () => {
		if (state?.section) {
			navigate(`/?section=${state?.section}`, {
				replace: true,
			});
			return;
		}

		navigate(`/?section=library`, { replace: true });
	};
	useValidateFavorite({
		id,
		favorites,
		onBack,
	});
	return (
		<FavoriteArticleContext.Provider
			value={{
				fontSize,
				setFontSize,
				isMenuOpen,
				setIsMenuOpen,
				article,
				toggleMenu: () => {
					setIsMenuOpen(!isMenuOpen);
				},
				isLoading,
				error,
				onBack,
			}}
		>
			<View />
			<Menu isOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen}>
				<Button.Favorite
					article={article}
					favorites={favorites}
					setFavorites={setFavorites}
				/>
				<Button.About />
				<Button.Font fontSize={fontSize} setFontSize={setFontSize} />
			</Menu>
		</FavoriteArticleContext.Provider>
	);
};

function View() {
	return (
		<div className={styles['view']}>
			<Topbar />
			<Content />
		</div>
	);
}

function Topbar() {
	return (
		<div className={styles['topbar']}>
			<div>
				<BackButton />
			</div>
			<div className={styles['title']}>
				<img src={NAVBAR_ICON_URL} alt="Logo" />
			</div>
			<div>
				<MenuButton />
			</div>
		</div>
	);
}

function BackButton() {
	const { onBack } = useContext(FavoriteArticleContext);
	return (
		<button
			type="button"
			onClick={() => {
				onBack();
			}}
		>
			<Icon name={'arrow-left'} color={'#000'} />
		</button>
	);
}

function MenuButton() {
	const { toggleMenu, isLoading } = useContext(FavoriteArticleContext);
	if (isLoading) {
		return null;
	}
	return (
		<button
			type="button"
			className={['tooltip', 'down', styles['menu']].join(' ')}
			data-text={'Menu'}
			onClick={() => {
				toggleMenu();
			}}
		>
			<Icon id={'menu-btn'} name={'more-horizontal'} color={'#3d3d3d'} />
		</button>
	);
}

function Content() {
	const { isLoading, error } = useContext(FavoriteArticleContext);
	if (isLoading) {
		return <LoadingView />;
	}
	if (error) {
		return <ErrorView />;
	}

	return <Article />;
}

function LoadingView() {
	return (
		<div className={styles['content']}>
			<Loading />
		</div>
	);
}

function ErrorView() {
	const { error } = useContext(FavoriteArticleContext);
	const classNames = [styles['content'], styles['error']];
	return (
		<div className={classNames.join(' ')}>
			<p>{error?.message}</p>
		</div>
	);
}

function Article() {
	const location = useLocation();
	const [isLightboxOpen, setIsLightboxOpen] = useState<boolean>(false);
	const [lighboxSlides, setLighboxSlides] = useState<Array<Slide>>([]);
	const { lang, device } = useContext(AppContext);
	const { stylesMap } = useContext(AssetsContext);
	const { article, fontSize } = useContext(FavoriteArticleContext);
	const [content, setContent] = useState<any>(null);
	const { css, classNameMap } = useStyle([article], stylesMap);

	useTrackingPageView({
		title: article?.name[lang],
		location: location.pathname,
	});

	const onLighboxOpen = (args: LightboxArgs) => {
		const { src, caption } = args;
		console.log(`I OPEN LIGHTBOX HERE`, args);
		setLighboxSlides([
			{
				src,
				description: caption,
			},
		]);
		setIsLightboxOpen(true);
	};

	useEffect(() => {
		if (!css) {
			return;
		}
		if (!article) {
			return;
		}
		setTimeout(() => {
			setContent(
				<VirtualArticle
					article={article}
					onLighboxOpen={onLighboxOpen}
					index={0}
					lang={lang}
					isSelected={true}
					device={device?.type || 'mobile'}
					className={classNameMap.get(`${article.id}`)}
					setNavbarVisibility={() => {}}
				/>,
			);
		}, 100);
	}, [css]);

	return (
		<article
			className={styles['content']}
			style={{ fontSize: fontSize ? `${fontSize}%` : undefined }}
		>
			<HeadHelmet />
			{content}
			<Lightbox
				carousel={{ finite: lighboxSlides.length <= 1 }}
				plugins={[Captions, Zoom, Fullscreen]}
				open={isLightboxOpen}
				close={() => setIsLightboxOpen(false)}
				slides={lighboxSlides}
				render={{
					buttonPrev:
						lighboxSlides.length <= 1 ? () => null : undefined,
					buttonNext:
						lighboxSlides.length <= 1 ? () => null : undefined,
				}}
			/>
			<style>{css}</style>
		</article>
	);
}

function HeadHelmet() {
	const { lang } = useContext(AppContext);
	const { article } = useContext(FavoriteArticleContext);
	if (!article) {
		return null;
	}
	const { name, thumbnail } = article;
	const title = name?.[lang];
	const keywords =
		article?.keywords && article.keywords.length ? article.keywords : [];
	const keywordsString = [...new Set(keywords)].join(',');
	return (
		<Helmet>
			<meta property="og:type" content={'article'} />
			{title ? <meta property="og:title" content={title} /> : null}
			<meta property="og:url" content={window.location.href} />
			{thumbnail ? (
				<meta property="og:image" content={thumbnail} />
			) : null}
			<meta property="og:category" content="digital-editions" />
			{keywordsString.length ? (
				<meta name="keywords" content={keywordsString}></meta>
			) : null}
			{keywordsString.length ? (
				<meta name="post-tags" content={keywordsString}></meta>
			) : null}
		</Helmet>
	);
}

export default Favorite;
